import React from "react";
import { useLocation } from "react-router-dom";
import SidebarNavItem from "@/components/dashboard/SidebarNavItem";

type SidebarNavGroupItem = {
  id: string;
  label: string;
  to: string;
};

type SidebarNavGroupProps = {
  id: string;
  label: string;
  items: SidebarNavGroupItem[];
};

export default function SidebarNavGroup({
  id,
  label,
  items,
}: SidebarNavGroupProps) {
  const location = useLocation();

  const hasActiveChild = React.useMemo(
    () =>
      items.some(
        (item) =>
          location.pathname === item.to ||
          location.pathname.startsWith(`${item.to}/`)
      ),
    [items, location.pathname]
  );

  const [isOpen, setIsOpen] = React.useState<boolean>(hasActiveChild);

  React.useEffect(() => {
    if (hasActiveChild) {
      setIsOpen(true);
    }
  }, [hasActiveChild]);

  return (
    <section className="space-y-2" aria-labelledby={`nav-group-${id}`}>
      <button
        id={`nav-group-${id}`}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className={[
          "flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-black uppercase tracking-[0.2em] transition",
          hasActiveChild
            ? "bg-slate-100 text-slate-900"
            : "text-slate-500 hover:bg-slate-100",
        ].join(" ")}
      >
        <span>{label}</span>
        <span className="text-[10px]">{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen ? (
        <div className="space-y-1 pl-1">
          {items.map((item) => (
            <SidebarNavItem key={item.id} label={item.label} to={item.to} />
          ))}
        </div>
      ) : null}
    </section>
  );
}